import { filterClasses } from "@swivl-mono/helpers";
import { PolicyReviewGroup } from "@swivl-mono/types";
import { ChipSelect, SelectItem, SelectOutlet } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { FaLocationDot } from "react-icons/fa6";
import { FiCornerDownRight } from "react-icons/fi";
import { IoIosArrowDown } from "react-icons/io";

import { locationSublabel } from "./LocationOptions";
import { displayPolicyValue } from "./PolicyUpdatesList";
import { CompactPolicyValueControl, FreeValueInput } from "./PolicyValueControl";

interface AffectedLocationsFieldProps {
  /** A `scope: "locations"` group — the caller renders the org-scope case separately (no location list). */
  group: PolicyReviewGroup;
  /** Every org location, grouped by org group — not just this group's current members, so an
   *  operator can add a location the scanned document never covered. */
  allLocationOptions: SelectItem[];
  /** True while a mutation for this group is in flight — disables the control. */
  busy: boolean;
  /** Sets every one of `locationIds` to `excluded` in one call (a Done click can both include and exclude). */
  onToggleExcluded: (locationIds: number[], excluded: boolean) => void;
  /** Checked locations that weren't a group member at all — adds them (creates their finding). */
  onAddLocations: (locationIds: number[]) => void;
}

/**
 * "Affects N locations" as a real multi-select — the same ChipSelect + customTrigger + staged-draft
 * composition as Ticketing's SubscribersField, sourced from every org location (grouped by org
 * group, address sublines), not just this update's current members. Checking an existing member
 * includes it, unchecking excludes it ("Skip this one"); checking a location that was never a
 * member at all ADDS it to this update. A mixed Done commit can fire up to three calls: exclude
 * some, re-include others, add brand-new ones. Pairs with `AffectedLocationsDetail` below, which
 * shows what each location's value changes to — this control is purely who's included.
 */
export function AffectedLocationsField({
  group,
  allLocationOptions,
  busy,
  onToggleExcluded,
  onAddLocations
}: AffectedLocationsFieldProps): React.ReactElement {
  const memberIds = new Set(group.locations.map((location) => String(location.locationId)));
  const includedIds = group.locations.filter((location) => !location.excluded).map((location) => String(location.locationId));
  const [selected, setSelected] = useState<string[]>(includedIds);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    // Don't clobber an in-progress edit; resync once the group's server state changes and settles.
    if (isEditing) return;
    setSelected(includedIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.locations, isEditing]);

  const handleChange = (values: string[]): void => {
    setIsEditing(true);
    setSelected(values);
  };

  const handleAccept = (): void => {
    setIsEditing(false);
    const currentIncluded = new Set(includedIds);
    const nextIncluded = new Set(selected);
    const newlyExcluded = [...currentIncluded].filter((id) => !nextIncluded.has(id)).map(Number);
    const newlyIncludedExisting = [...nextIncluded].filter((id) => !currentIncluded.has(id) && memberIds.has(id)).map(Number);
    const newlyAdded = [...nextIncluded].filter((id) => !memberIds.has(id)).map(Number);
    if (newlyExcluded.length > 0) onToggleExcluded(newlyExcluded, true);
    if (newlyIncludedExisting.length > 0) onToggleExcluded(newlyIncludedExisting, false);
    if (newlyAdded.length > 0) onAddLocations(newlyAdded);
  };

  const handleOpenChange = (open: boolean): void => {
    // Closing without Accept cancels the draft; let the resync effect catch up.
    if (!open) setIsEditing(false);
  };

  const count = selected.length;

  return (
    <ChipSelect
      styles={{ [SelectOutlet.POPOVER]: { minWidth: 320 } }}
      hasSearchInDropdown
      disabled={busy}
      options={allLocationOptions}
      selectedValues={selected}
      onChange={handleChange}
      onAccept={handleAccept}
      onOpenChange={handleOpenChange}
      customTrigger={({ isOpen }) => (
        <button
          type="button"
          disabled={busy}
          className={filterClasses(
            "inline-flex h-6 items-center gap-1.5 justify-self-start rounded-full border px-2.5 text-[12px] font-bold whitespace-nowrap transition-colors",
            isOpen ? "border-grey-900 bg-grey-900 text-white" : "border-orange-100 bg-orange-50 text-orange-700 hover:bg-orange-100",
            busy && "cursor-not-allowed opacity-60"
          )}
        >
          <FaLocationDot className="size-3" />
          {`Affects ${count} location${count === 1 ? "" : "s"}`}
          <IoIosArrowDown className={filterClasses("size-2.5 transition-transform", isOpen && "rotate-180")} />
        </button>
      )}
    />
  );
}

interface AffectedLocationsDetailProps {
  /** A `scope: "locations"` group — the caller renders the org-scope case separately (no location list). */
  group: PolicyReviewGroup;
  /** "Describe further" (text-type) children folded under `group` (nestGroups) — e.g. "Other" lock
   * style's own describe-text. Rendered as an indented row directly under the location it applies
   * to, so filling it in never requires leaving this per-location breakdown. */
  childGroups?: PolicyReviewGroup[];
  /** True while a mutation for this group is in flight — disables every location's control. */
  busy: boolean;
  /** A single location's own edit — independent of its siblings. This is the ONLY place a
   * single-/multi-select field can be corrected once its group covers more than one location (its
   * real widget doesn't fit the dense "All updates" row) — live for every location, not just when
   * they currently disagree, so a genuine per-location correction is always possible. `targetGroup`
   * omitted = editing `group` itself; present = editing one of `childGroups` instead. */
  onEditValue: (value: string, locationId: number, targetGroup?: PolicyReviewGroup) => void;
}

// `divide-x` gives every column a real vertical rule instead of relying on `gap` alone.
const DETAIL_GRID = "grid grid-cols-[minmax(160px,1.3fr)_minmax(0,1fr)_18px_minmax(0,1.3fr)] items-center gap-3 divide-x divide-grey-100";

/**
 * Per-location "what changed" — name · today · → · after apply, one row per location. Today's
 * value is read-only; "After apply" is each location's OWN edit, bound to `location.proposedValue`
 * (not the group's, which may be null — "Varies by location" — when locations disagree). A toggle
 * or text/number field shows its value directly, click-to-change; a single-/multi-select shows only
 * the option(s) that currently apply — never the full list for every location at once — and opens
 * its real radio/checkbox picker in a popover on click (CompactPolicyValueControl). An excluded
 * location shows "Unchanged" instead; it's staying put regardless of what its value says.
 */
export function AffectedLocationsDetail({ group, childGroups = [], busy, onEditValue }: AffectedLocationsDetailProps): React.ReactElement {
  // Same rule PolicyUpdatesList uses for the main row: only a "describe further" (free text) child
  // gets folded inline here — a genuinely distinct child question keeps its own separate group/step.
  const textChildren = childGroups.filter((child) => child.fieldType === "text");

  return (
    <div className="overflow-hidden rounded-[5px] border border-grey-200 bg-grey-50">
      <div className={filterClasses(DETAIL_GRID, "border-b border-grey-200 px-4 py-2")}>
        <span className="text-[10px] font-bold tracking-[0.06em] text-grey-500 uppercase">Location</span>
        <span className="text-[10px] font-bold tracking-[0.06em] text-grey-500 uppercase">Today</span>
        <span />
        <span className="text-[10px] font-bold tracking-[0.06em] text-grey-500 uppercase">After apply</span>
      </div>
      {group.locations.map((location) => {
        const sublabel = locationSublabel(location);
        const willChange = !location.excluded && location.currentValue !== location.proposedValue;
        return (
          <div key={location.locationId}>
            <div className={filterClasses(DETAIL_GRID, "border-b border-grey-200 px-4 py-2.5")}>
              <div className="flex min-w-0 flex-col">
                <span className={filterClasses("truncate text-[13px] font-semibold", location.excluded ? "text-grey-500" : "text-grey-900")}>
                  {location.locationName}
                </span>
                {sublabel && <span className="truncate text-[11px] text-grey-500">{sublabel}</span>}
              </div>

              {location.currentValueIsDefault && (location.citation || location.rawExtracted) ? (
                // Nothing was really answered here — show the legacy text that justifies the proposed
                // value instead of a bare "we don't know," so this otherwise-empty cell answers "where
                // did this come from" for this specific location.
                <span
                  className="text-[13px] [overflow-wrap:anywhere] text-grey-500 italic"
                  title="The legacy text this proposed value was extracted from"
                >
                  “{location.citation || location.rawExtracted}”
                </span>
              ) : location.currentValueIsDefault ? (
                <span className="text-[13px] text-grey-500 italic">Not previously set</span>
              ) : !location.currentValue ? (
                <span className="text-[13px] text-grey-500 italic">Not set</span>
              ) : (
                <span
                  className={filterClasses("text-[13px] [overflow-wrap:anywhere] text-grey-500", willChange && "line-through decoration-grey-400")}
                >
                  {displayPolicyValue(location.currentValue, group.fieldType, group.options)}
                </span>
              )}

              <span className={filterClasses("text-center text-[13px]", willChange ? "text-orange-500" : "text-grey-400")}>→</span>

              {location.excluded ? (
                <span className="text-[13px] text-grey-500 italic">Unchanged</span>
              ) : (
                <CompactPolicyValueControl
                  fieldType={group.fieldType}
                  options={group.options}
                  value={location.proposedValue}
                  busy={busy}
                  onChange={(value) => onEditValue(value, location.locationId)}
                />
              )}
            </div>

            {/* Skipped when currentValueIsDefault — that case already shows this same source text in
             * the Today cell above, so this row would just repeat it. Shown here for a location that
             * DID have a real prior answer, so an operator can see what's justifying the proposed
             * CHANGE without that overwriting the Today column's real value. */}
            {!location.excluded && !location.currentValueIsDefault && location.rawExtracted && (
              <div className={filterClasses(DETAIL_GRID, "border-b border-grey-200 bg-white px-4 py-1.5 last:border-b-0")}>
                <span />
                <span className="col-span-3 text-[11px] [overflow-wrap:anywhere] text-grey-500 italic" title={location.rawExtracted}>
                  “{location.rawExtracted}”
                  {location.citation && location.citation !== location.rawExtracted && (
                    <span className="text-grey-400 not-italic"> — {location.citation}</span>
                  )}
                </span>
              </div>
            )}

            {!location.excluded &&
              textChildren.map((child) => {
                const childLocation = child.locations.find((l) => l.locationId === location.locationId);
                if (!childLocation) return null;
                return (
                  <div
                    key={child.groupKey}
                    className={filterClasses(DETAIL_GRID, "border-b border-l-2 border-grey-200 bg-white py-2 last:border-b-0")}
                  >
                    {/* Indent lives inside this one cell only — the other columns (today/arrow/
                     * after-apply) stay aligned with the parent location row's same columns. */}
                    <div className="flex min-w-0 items-center gap-1 pl-4">
                      <FiCornerDownRight className="size-3 shrink-0 text-grey-400" />
                      <span className="truncate text-[12px] text-grey-500">{child.label}</span>
                    </div>
                    <span />
                    <span className="text-center text-[13px] text-grey-400">→</span>
                    {childLocation.excluded ? (
                      <span className="text-[13px] text-grey-500 italic">Unchanged</span>
                    ) : (
                      <FreeValueInput
                        fieldType={child.fieldType}
                        options={child.options}
                        value={childLocation.proposedValue}
                        busy={busy}
                        onChange={(value) => onEditValue(value, location.locationId, child)}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
}
